/**
 * A business's own page — the same private link as a homeowner's
 * (/my/:token), a different page (owner, Oct 20 2026: "I want the customer
 * portal of a commercial client to be way different than a residential
 * client").
 *
 * A property manager opens it for four things, so it is four tabs: Home (the
 * next visit, what is owed, what is waiting on them), Sites (each site, its
 * rhythm, its contact, a way to flag a problem there), Billing (terms, PO,
 * open bills to pay, past bills), Papers (COI, W-9, signed agreements). No
 * rewards, no referrals, no lawn plans, no "your yard". Every visit that has
 * a photo report links to it. The data is services/businessPortal on the
 * server; this page only draws it.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import {
  Calendar, Receipt, FileSignature, MapPin, ChevronRight, MessageSquare, Camera, X, ShieldCheck, FileText,
  CheckCircle2, AlertTriangle, Download, Building2, CreditCard,
} from 'lucide-react';
import { publicApi } from '../services/api';
import { money as moneyCents, longDate, shortDate, clock } from '../utils/format';
import { companyOf, DEFAULT_COMPANY } from '../utils/company';
import { absUrl, daysUntil, docKindLabel, expiryTone } from '../utils/commercial';
import { PublicFooter, H1, ButtonPrimary, ButtonGhost } from '../components/public';

type Tab = 'home' | 'sites' | 'billing' | 'papers';
const money = (n: number) => (Math.abs(n - Math.round(n)) < 0.005 ? `$${Math.round(n).toLocaleString('en-US')}` : moneyCents(n));
const weekday = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '');

function Card({ title, icon: Icon, children, tone = 'plain', action }: {
  title?: string; icon?: React.ElementType; children: React.ReactNode; tone?: 'plain' | 'brand' | 'warn'; action?: React.ReactNode;
}) {
  return (
    <section className={`rounded-3xl border p-5 shadow-soft ${tone === 'brand' ? 'border-brand-300 bg-brand-100' : tone === 'warn' ? 'border-amber-300 bg-amber-50' : 'border-brand-100 bg-white'}`}>
      {title && (
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-black uppercase tracking-tight flex items-center gap-2 text-gray-900">
            {Icon && <Icon className="h-5 w-5 text-brand-600" aria-hidden />} {title}
          </h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export default function BusinessPortal({ data, refetch }: { data: any; refetch: () => void }) {
  const { token } = useParams<{ token: string }>();
  const [tab, setTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'sites' || t === 'billing' || t === 'papers' ? t : 'home';
  });
  const [texting, setTexting] = useState<string | null>(null);   // the message box, with a first line already in it
  const [caring, setCaring] = useState<number | null | false>(false);   // the problem sheet, for a site (or any)
  const company = companyOf(data);
  const a = data.account;
  const sites: any[] = data.sites || [];
  const upcoming: any[] = data.upcoming || [];
  const recent: any[] = data.recent || [];
  const invoices: any[] = data.invoices || [];
  const open = invoices.filter(i => i.payable);
  const agreements: any[] = data.agreements || [];
  const waiting = agreements.filter(q => q.needs_signature);
  const next = upcoming[0];
  const TABS: { key: Tab; label: string }[] = [
    { key: 'home', label: 'Home' }, { key: 'sites', label: `Sites${sites.length ? ` · ${sites.length}` : ''}` },
    { key: 'billing', label: 'Billing' }, { key: 'papers', label: 'Papers' },
  ];

  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <header className="bg-brand-600 text-black sticky top-0 z-30 shadow-brand">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-3">
          <span className="flex-shrink-0 h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">
            <img src="/logo-white.png" alt="" width={32} height={32} className="h-8 w-8 object-contain" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-black uppercase tracking-tight leading-none text-[14px] whitespace-nowrap text-white">Keep Austin Mowed</p>
            <p className="text-[13px] text-white/85 truncate mt-1">{a.company}</p>
          </div>
          <button onClick={() => setTexting('')}
            className="press flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-brand-700 px-3.5 h-11 rounded-2xl shadow-soft text-[11px] font-black uppercase tracking-wide">
            <MessageSquare className="h-4 w-4" aria-hidden /> Message
          </button>
        </div>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 flex gap-1 border-t border-white/20 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} aria-current={tab === t.key ? 'page' : undefined}
              className={`press px-4 min-h-[50px] text-[15px] font-black uppercase tracking-tight border-b-[3px] whitespace-nowrap ${tab === t.key ? 'border-white text-white' : 'border-transparent text-white/60'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        {tab === 'home' && (
          <>
            <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
              <div className="px-5 pt-5 pb-1">
                <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">Your account</p>
                <H1>{a.company}</H1>
                <p className="text-[15px] text-gray-700 mt-1">
                  {sites.filter(s => !s.cancelled).length} site{sites.filter(s => !s.cancelled).length === 1 ? '' : 's'}
                  {a.contact ? ` · ${a.contact}` : ''} · net {a.terms}{a.po ? ` · PO ${a.po}` : ''}
                </p>
              </div>
              <div className="px-5 py-6 mt-3 bg-brand-600 text-black">
                <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Next visit</p>
                {next ? (
                  <>
                    <p className="text-2xl font-black tracking-tight mt-1">{longDate(next.date)}{next.time ? ` · ${clock(next.time)}` : ''}</p>
                    <p className="text-[15px] font-medium text-black/80 mt-1">
                      {next.service} at <b>{next.site}</b>.
                      {upcoming.length > 1 ? ` ${upcoming.length - 1} more visit${upcoming.length === 2 ? '' : 's'} booked after it.` : ''}
                    </p>
                  </>
                ) : (
                  <p className="text-[17px] font-medium text-black/80 mt-1">Nothing booked right now. Message us and we will get your sites on the calendar.</p>
                )}
              </div>
              {open.length > 0 && (
                <div className="px-5 py-5 border-t-2 border-brand-100">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] font-black uppercase tracking-tight text-gray-900">{open.length === 1 ? 'Open invoice' : `${open.length} open invoices`}</span>
                    <span className="text-3xl font-black tracking-tight text-brand-600 tabular-nums">{money(data.open_total)}</span>
                  </div>
                  {open.some(i => i.overdue) && <p className="text-[14px] font-bold text-red-700 mt-1 inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {open.filter(i => i.overdue).length === open.length ? 'Past the due date.' : `${open.filter(i => i.overdue).length} past the due date.`}</p>}
                  <div className="mt-4 space-y-2">
                    {open.map(i => (
                      <ButtonPrimary key={i.id} href={i.link || '#'}>
                        <CreditCard className="h-5 w-5" aria-hidden /> Pay {money(i.owed)}{open.length > 1 ? ` · ${i.number}` : ''}
                      </ButtonPrimary>
                    ))}
                  </div>
                  <p className="text-[13px] text-gray-600 mt-2 text-center">Card or bank on the invoice page. Paying by check? The remit address is on the invoice.</p>
                </div>
              )}
            </section>

            {waiting.length > 0 && (
              <Card title="Waiting on your signature" icon={FileSignature} tone="brand">
                <ul className="space-y-2">
                  {waiting.map(q => (
                    <li key={q.id}>
                      <a href={q.link || '#'} className="press flex items-center gap-3 rounded-2xl border-2 border-brand-300 bg-white shadow-soft px-4 py-4 min-h-[64px]">
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-bold text-gray-900">{q.title}</p>
                          <p className="text-[14px] text-gray-700">{q.site ? `${q.site} · ` : ''}sent {shortDate(q.sent_on)}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                      </a>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card title="Coming up" icon={Calendar}>
              {upcoming.length === 0 ? <p className="text-[15px] text-gray-700">Nothing booked yet.</p> : (
                <ul className="divide-y divide-gray-200">
                  {upcoming.slice(0, 12).map(v => (
                    <li key={v.id} className="py-3 first:pt-0 last:pb-0 flex items-baseline gap-3">
                      <span className="text-[15px] font-bold text-gray-900 w-28 flex-shrink-0 tabular-nums">{weekday(v.date)}</span>
                      <span className="flex-1 min-w-0 text-[15px] text-gray-800">
                        <span className="font-semibold text-gray-900">{v.site}</span> · {v.service}{v.time ? ` · ${clock(v.time)}` : ''}{v.in_progress ? ' · on site now' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Visits done" icon={Camera}>
              {recent.length === 0 ? <p className="text-[15px] text-gray-700">No visits yet. Each one you get a photo report for shows up here.</p> : (
                <ul className="divide-y divide-gray-200">
                  {recent.slice(0, 12).map(v => <VisitRow key={v.id} v={v} />)}
                </ul>
              )}
              {a.reports_on && <p className="text-[13px] text-gray-600 mt-3">A photo report is emailed to your site contact about half an hour after each visit.</p>}
            </Card>
          </>
        )}

        {tab === 'sites' && (
          <>
            {sites.map(s => {
              const done = recent.filter(v => v.site_id === s.id);
              const nextHere = upcoming.find(v => v.site_id === s.id);
              return (
                <section key={s.id} className={`rounded-3xl border shadow-soft overflow-hidden ${s.cancelled ? 'border-gray-200 bg-gray-50' : 'border-brand-100 bg-white'}`}>
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-start gap-3">
                      <span className="h-10 w-10 rounded-2xl bg-brand-600 text-black flex items-center justify-center flex-shrink-0"><Building2 className="h-5 w-5" aria-hidden /></span>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-black tracking-tight text-gray-900 leading-tight">{s.name}{s.cancelled ? <span className="ml-2 text-[12px] font-bold uppercase text-gray-500">not in service</span> : null}</h2>
                        <p className="text-[14px] text-gray-700 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" aria-hidden /> {[s.address, s.city, s.zip_code].filter(Boolean).join(', ')}</p>
                      </div>
                    </div>
                    <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[14px]">
                      <div><dt className="text-[11px] font-black uppercase tracking-wide text-gray-500">Next</dt><dd className="font-bold text-gray-900">{nextHere ? weekday(nextHere.date) : s.next_visit ? weekday(s.next_visit) : '—'}</dd></div>
                      <div><dt className="text-[11px] font-black uppercase tracking-wide text-gray-500">Rhythm</dt><dd className="font-bold text-gray-900">{s.rhythm || '—'}</dd></div>
                      <div><dt className="text-[11px] font-black uppercase tracking-wide text-gray-500">Last</dt><dd className="font-bold text-gray-900">{s.last_visit ? weekday(s.last_visit) : '—'}</dd></div>
                      <div><dt className="text-[11px] font-black uppercase tracking-wide text-gray-500">Visits</dt><dd className="font-bold text-gray-900 tabular-nums">{s.visits_done}</dd></div>
                    </dl>
                    <p className="mt-3 text-[14px] text-gray-700">
                      {s.contact ? <>Site contact: <b className="text-gray-900">{[s.contact.name, s.contact.phone, s.contact.email].filter(Boolean).join(' · ')}</b></>
                        : <span className="text-amber-800 font-semibold">No site contact on file — tell us who should get this site's reports.</span>}
                    </p>
                  </div>
                  {done.length > 0 && (
                    <div className="px-5 pb-4">
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-500 mb-1">Recent visits here</p>
                      <ul className="divide-y divide-gray-200">{done.slice(0, 5).map(v => <VisitRow key={v.id} v={v} noSite />)}</ul>
                    </div>
                  )}
                  <div className="px-5 pb-5 grid grid-cols-2 gap-2">
                    <ButtonGhost onClick={() => setTexting(`${s.name}: `)} className="!text-[13px] !px-3 !min-h-[48px]">Ask about this site</ButtonGhost>
                    <ButtonGhost onClick={() => setCaring(s.id)} className="!text-[13px] !px-3 !min-h-[48px]">Something wrong here?</ButtonGhost>
                  </div>
                </section>
              );
            })}
            <Card tone="brand">
              <p className="text-[15px] text-gray-900">Another property to add? <button type="button" onClick={() => setTexting('New site: ')} className="font-bold text-brand-700 underline">Send us the address</button> and we will walk it and send a proposal.</p>
            </Card>
          </>
        )}

        {tab === 'billing' && (
          <>
            <Card title="How you are billed" icon={Receipt}>
              <ul className="space-y-2 text-[15px] text-gray-800">
                <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden /><span>
                  {a.pricing === 'monthly' && a.contract_monthly
                    ? <>A flat <b className="text-gray-900">{money(a.contract_monthly)} a month</b> covers the regular visits{a.contract_end ? `, through ${longDate(a.contract_end)}${a.contract_auto_renew ? ' and renewing' : ''}` : ''}. Extra work bills on top.</>
                    : <>One invoice at the end of each month for the visits done that month.</>}
                </span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden /><span><b className="text-gray-900">Net {a.terms}</b>{a.po ? <> · PO <b className="text-gray-900">{a.po}</b> on every invoice</> : ' · tell us if you need a PO on your invoices'}.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden /><span>
                  Invoices go to {a.billing_email ? <b className="text-gray-900">{a.billing_email}</b> : a.email ? <b className="text-gray-900">{a.email}</b> : 'the address on file'}
                  {(data.contacts || []).filter((x: any) => x.gets_bills && x.email).length ? <> and {(data.contacts || []).filter((x: any) => x.gets_bills && x.email).map((x: any) => x.name || x.email).join(', ')}</> : null}.
                  {' '}<button type="button" onClick={() => setTexting('Billing contact change: ')} className="font-bold text-brand-700 underline">Change who gets them</button>
                </span></li>
                {a.billing_address && <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden /><span>Bill to: {a.billing_address}</span></li>}
              </ul>
            </Card>

            <Card title={open.length ? `Open · ${money(data.open_total)}` : 'Open invoices'} icon={CreditCard} tone={open.some(i => i.overdue) ? 'warn' : 'plain'}>
              {open.length === 0 ? <p className="text-[15px] text-gray-700">Nothing open. Thank you.</p> : (
                <ul className="space-y-3">
                  {open.map(i => (
                    <li key={i.id} className="rounded-2xl border-2 border-brand-200 bg-white p-4">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-[15px] font-bold text-gray-900">{i.number}{i.po ? <span className="font-normal text-gray-600"> · PO {i.po}</span> : null}</p>
                        <p className="text-xl font-black text-gray-900 tabular-nums">{money(i.owed)}</p>
                      </div>
                      <p className="text-[14px] text-gray-700">
                        {i.period ? `${shortDate(i.period.start)} – ${shortDate(i.period.end)}` : `sent ${shortDate(i.sent_on)}`}
                        {i.due_date ? <> · <span className={i.overdue ? 'font-bold text-red-700' : ''}>due {shortDate(i.due_date)}</span></> : null}
                        {i.paid > 0 ? ` · ${money(i.paid)} paid` : ''}
                      </p>
                      <div className="mt-3"><ButtonPrimary href={i.link || '#'}>View &amp; pay</ButtonPrimary></div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Past invoices" icon={FileText}>
              {invoices.filter(i => !i.payable).length === 0 ? <p className="text-[15px] text-gray-700">None yet.</p> : (
                <ul className="divide-y divide-gray-200">
                  {invoices.filter(i => !i.payable).map(i => (
                    <li key={i.id}>
                      <a href={i.link || '#'} className="press py-3 flex items-center gap-3 min-h-[56px]">
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] text-gray-900"><span className="font-bold tabular-nums">{money(i.total)}</span><span className="text-gray-700"> · {i.number}{i.po ? ` · PO ${i.po}` : ''}</span></p>
                          <p className="text-[13px] text-gray-600">{i.period ? `${shortDate(i.period.start)} – ${shortDate(i.period.end)}` : shortDate(i.sent_on)}{i.paid_at ? ` · paid ${shortDate(i.paid_at)}` : ''}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}

        {tab === 'papers' && (
          <>
            <Card title="On file" icon={ShieldCheck} action={
              <button type="button" onClick={() => setTexting('Please send an updated COI / W-9: ')} className="text-[13px] font-bold text-brand-700 underline">Need a current one?</button>
            }>
              {(data.documents || []).length === 0 ? (
                <p className="text-[15px] text-gray-700">Nothing uploaded yet. Need our certificate of insurance or W-9? <button type="button" onClick={() => setTexting('Please send your COI and W-9: ')} className="font-bold text-brand-700 underline">Ask and we will put them here.</button></p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {(data.documents || []).map((d: any) => {
                    const tone = expiryTone(d.expires_on); const n = daysUntil(d.expires_on);
                    return (
                      <li key={d.id} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                        <span className="h-10 w-10 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5" aria-hidden /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-bold text-gray-900 truncate"><span className="text-brand-700">{docKindLabel(d.kind)}</span>{d.title && d.title.trim().toLowerCase() !== docKindLabel(d.kind).toLowerCase() ? ` · ${d.title}` : ''}</p>
                          <p className={`text-[13px] ${tone === 'expired' ? 'text-red-700 font-bold' : tone === 'soon' ? 'text-amber-700 font-bold' : 'text-gray-600'}`}>
                            {tone === 'none' ? `added ${shortDate(d.added_on)}` : tone === 'expired' ? `expired ${shortDate(d.expires_on)} — ask us for the new one` : tone === 'soon' ? `expires ${shortDate(d.expires_on)} (${n} days)` : `valid to ${shortDate(d.expires_on)}`}
                          </p>
                        </div>
                        <a href={absUrl(d.url)} target="_blank" rel="noopener noreferrer" className="press inline-flex items-center gap-1.5 text-[13px] font-black uppercase tracking-wide text-brand-700 min-h-[44px] px-2">
                          <Download className="h-4 w-4" aria-hidden /> Open
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Agreements" icon={FileSignature}>
              {agreements.length === 0 ? <p className="text-[15px] text-gray-700">No proposals yet.</p> : (
                <ul className="divide-y divide-gray-200">
                  {agreements.map(q => (
                    <li key={q.id}>
                      <a href={q.link || '#'} className="press py-3 flex items-center gap-3 min-h-[56px]">
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-bold text-gray-900 truncate">{q.title}</p>
                          <p className="text-[13px] text-gray-600">
                            {q.site ? `${q.site} · ` : ''}
                            {q.signed ? <span className="text-emerald-700 font-semibold">signed{q.signed_by ? ` by ${q.signed_by}` : ''}{q.signed_on ? ` on ${shortDate(q.signed_on)}` : ''}</span>
                              : q.needs_signature ? <span className="text-brand-700 font-semibold">waiting on your signature</span>
                              : q.status === 'sent' ? `sent ${shortDate(q.sent_on)}` : 'accepted'}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2">
          <ButtonGhost onClick={() => setTexting('')} className="!text-[13px] !px-3"><MessageSquare className="h-5 w-5" aria-hidden /> Message us</ButtonGhost>
          <ButtonGhost onClick={() => setCaring(null)} className="!text-[13px] !px-3">Something wrong?</ButtonGhost>
        </div>
      </main>

      <PublicFooter company={company} />

      {texting !== null && <MessageSheet token={token!} start={texting} onClose={() => setTexting(null)} />}
      {caring !== false && <ProblemSheet token={token!} data={data} siteId={caring} onClose={() => { setCaring(false); refetch(); }} />}
    </div>
  );
}

function VisitRow({ v, noSite }: { v: any; noSite?: boolean }) {
  const inner = (
    <>
      <span className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0"><CheckCircle2 className="h-4 w-4" aria-hidden /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] text-gray-900 truncate"><b>{weekday(v.date)}</b>{noSite ? '' : ` · ${v.site}`} · {v.service}</span>
        <span className="block text-[13px] text-gray-600">{v.report ? (v.photos ? `Photo report · ${v.photos} photo${v.photos === 1 ? '' : 's'}` : 'Report') : v.photos ? `${v.photos} photo${v.photos === 1 ? '' : 's'} · report on its way` : 'done'}</span>
      </span>
      {v.report && <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />}
    </>
  );
  const cls = 'press py-3 first:pt-0 last:pb-0 flex items-center gap-3 min-h-[56px]';
  return <li>{v.report ? <a href={v.report} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a> : <div className={cls}>{inner}</div>}</li>;
}

function MessageSheet({ token, start, onClose }: { token: string; start: string; onClose: () => void }) {
  const [text, setText] = useState(start);
  const [sent, setSent] = useState<string | null>(null);
  const send = useMutation(() => publicApi.post(`/portal/${token}/message`, { text }), {
    onSuccess: (r: any) => setSent(r.data.message),
    onError: (e: any) => setSent(e.response?.data?.message || `Could not send that. Please call ${DEFAULT_COMPANY.phone}.`),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-3 shadow-lift" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-black text-gray-900 uppercase tracking-tight">Message the office</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {sent ? (
          <>
            <p className="text-[15px] text-gray-800">{sent}</p>
            <ButtonPrimary onClick={onClose}>Done</ButtonPrimary>
          </>
        ) : (
          <>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={5} autoFocus
              placeholder="A new site to quote, a gate code that changed, a date to skip, a question on an invoice…"
              className="w-full rounded-2xl border-2 border-brand-200 px-4 py-3 text-[15px] focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
            <ButtonPrimary onClick={() => send.mutate()} disabled={!text.trim() || send.isLoading}>{send.isLoading ? 'Sending' : 'Send'}</ButtonPrimary>
            <p className="text-[13px] text-gray-600 text-center">Goes straight to the owner's phone.</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Something was not right at a site: which site, which visit, what happened, a photo if they have one. */
function ProblemSheet({ token, data, siteId, onClose }: { token: string; data: any; siteId: number | null; onClose: () => void }) {
  const kinds: any[] = data?.care_kinds || [];
  const sites: any[] = data?.sites || [];
  const [site, setSite] = useState<string>(siteId ? String(siteId) : '');
  const recent: any[] = (data?.recent || []).filter((v: any) => !site || String(v.site_id) === site).slice(0, 6);
  const [kind, setKind] = useState('');
  const [jobId, setJobId] = useState('');
  const [detail, setDetail] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const siteName = sites.find(s => String(s.id) === site)?.name;

  async function send() {
    setBusy(true);
    try {
      const text = `${siteName ? `[${siteName}] ` : ''}${detail}`;
      let r: any;
      if (photo) {
        const form = new FormData();
        form.append('kind', kind); form.append('detail', text);
        if (jobId) form.append('job_id', jobId);
        form.append('photo', photo);
        r = (await publicApi.post(`/portal/${token}/care`, form)).data;
      } else {
        r = (await publicApi.post(`/portal/${token}/care`, { kind, detail: text, job_id: jobId ? Number(jobId) : undefined })).data;
      }
      setSent(r.message);
    } catch (e: any) {
      setSent(e?.response?.data?.message || `That did not send. Please call ${DEFAULT_COMPANY.phone}.`);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-4 max-h-[88vh] overflow-y-auto shadow-lift" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-black text-gray-900 uppercase tracking-tight">Tell us what happened</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {sent ? (
          <>
            <p className="text-[15px] text-gray-800">{sent}</p>
            <ButtonPrimary onClick={onClose}>Close</ButtonPrimary>
          </>
        ) : (
          <>
            {sites.length > 1 && (
              <label className="block">
                <span className="text-[13px] font-bold text-gray-700">Which site?</span>
                <select value={site} onChange={e => { setSite(e.target.value); setJobId(''); }} className="mt-1 w-full rounded-2xl border-2 border-brand-200 px-3 py-2.5 text-[15px]">
                  <option value="">Not about one site</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            )}
            <div className="space-y-1.5">
              {kinds.map(k => (
                <button key={k.key} type="button" onClick={() => setKind(k.key)}
                  className={`press w-full text-left px-4 py-3 rounded-2xl border-2 text-[15px] ${kind === k.key ? 'border-brand-600 bg-brand-100 font-bold text-gray-900' : 'border-brand-200 text-gray-800'}`}>
                  {k.label}
                </button>
              ))}
            </div>
            {recent.length > 0 && (
              <label className="block">
                <span className="text-[13px] font-bold text-gray-700">Which visit? (optional)</span>
                <select value={jobId} onChange={e => setJobId(e.target.value)} className="mt-1 w-full rounded-2xl border-2 border-brand-200 px-3 py-2.5 text-[15px]">
                  <option value="">Not about one visit</option>
                  {recent.map(v => <option key={v.id} value={v.id}>{weekday(v.date)} · {v.site} · {v.service}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <span className="text-[13px] font-bold text-gray-700">What happened?</span>
              <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={4}
                placeholder="The strip along the back fence was left long, and the gate by unit 40 was open."
                className="mt-1 w-full rounded-2xl border-2 border-brand-200 px-4 py-3 text-[15px] focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-gray-700">Add a photo (optional)</span>
              <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-xl file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:font-bold file:text-brand-700" />
            </label>
            <ButtonPrimary onClick={send} disabled={busy || !kind || detail.trim().length < 4}>{busy ? 'Sending' : 'Send it'}</ButtonPrimary>
            <p className="text-[13px] text-gray-600 text-center">Straight to the owner's phone, with the site and the visit attached.</p>
          </>
        )}
      </div>
    </div>
  );
}
